// Clinical calculators panel. Sits inside the Investigations section.
// Auto-fills inputs from the current investigations & vitals in state, lets
// the doctor override, computes on the fly, and can push the result back as
// a new investigation row.

import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calculator, ChevronDown, ChevronRight, Plus, Info, Pin, PinOff, FileText } from "lucide-react";
import { canonicalKey } from "./TrendChip";

const PIN_STORAGE_KEY = "print_pinned_calcs_v1";
export function loadPinnedCalcs() {
  try { return JSON.parse(localStorage.getItem(PIN_STORAGE_KEY) || "[]"); } catch { return []; }
}
function savePinnedCalcs(arr) {
  try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(arr)); } catch (e) { console.debug("[silent]", e); }
}
// Keys the doctor has EXPLICITLY unpinned — auto-pin skips these so an
// unpinned calculator stays unpinned across recalculations and visits.
const UNPIN_STORAGE_KEY = "print_unpinned_calcs_v1";
function loadUnpinnedCalcs() {
  try { return new Set(JSON.parse(localStorage.getItem(UNPIN_STORAGE_KEY) || "[]")); } catch { return new Set(); }
}
function saveUnpinnedCalcs(set) {
  try { localStorage.setItem(UNPIN_STORAGE_KEY, JSON.stringify([...set])); } catch (e) { console.debug("[silent]", e); }
}

// -------------------- formulas --------------------

function num(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(",", "").trim();
  if (!s) return null;
  const n = parseFloat(s);
  return isFinite(n) ? n : null;
}

function round(n, d = 1) {
  if (n === null || n === undefined || !isFinite(n)) return null;
  const p = Math.pow(10, d);
  return Math.round(n * p) / p;
}

// CKD-EPI 2021 (race-free). Scr in mg/dL. sex "M" | "F".
export function ckdEpi2021(scr, age, sex) {
  scr = num(scr); age = num(age);
  if (!scr || !age || !sex) return null;
  const female = String(sex).toUpperCase().startsWith("F");
  const kappa = female ? 0.7 : 0.9;
  const alpha = female ? -0.241 : -0.302;
  const minTerm = Math.pow(Math.min(scr / kappa, 1), alpha);
  const maxTerm = Math.pow(Math.max(scr / kappa, 1), -1.200);
  let egfr = 142 * minTerm * maxTerm * Math.pow(0.9938, age);
  if (female) egfr *= 1.012;
  return round(egfr, 1);
}

// MDRD 4-variable with Indian modifier (Kher et al. 2005): × 1.212.
export function mdrdIndian(scr, age, sex) {
  scr = num(scr); age = num(age);
  if (!scr || !age || !sex) return null;
  const female = String(sex).toUpperCase().startsWith("F");
  let egfr = 175 * Math.pow(scr, -1.154) * Math.pow(age, -0.203) * 1.212;
  if (female) egfr *= 0.742;
  return round(egfr, 1);
}

// HOMA-IR = FBS(mg/dL) × Fasting Insulin(µU/mL) / 405
export function homaIR(fbs, fInsulin) {
  fbs = num(fbs); fInsulin = num(fInsulin);
  if (!fbs || !fInsulin) return null;
  return round((fbs * fInsulin) / 405, 2);
}
// HOMA-β = 20 × insulin / (FBS(mmol/L) − 3.5)     FBS mmol/L = mg/dL ÷ 18
export function homaBeta(fbs, fInsulin) {
  fbs = num(fbs); fInsulin = num(fInsulin);
  if (!fbs || !fInsulin) return null;
  const mmol = fbs / 18;
  if (mmol <= 3.5) return null;
  return round((20 * fInsulin) / (mmol - 3.5), 0);
}

// eAG (mg/dL) = 28.7 × HbA1c − 46.7
export function eAG(hba1c) {
  hba1c = num(hba1c);
  if (!hba1c) return null;
  return round(28.7 * hba1c - 46.7, 0);
}

// Friedewald LDL = TC − HDL − TG/5   (valid when TG < 400)
export function friedewaldLDL(tc, hdl, tg) {
  tc = num(tc); hdl = num(hdl); tg = num(tg);
  if (tc === null || hdl === null || tg === null) return null;
  if (tg >= 400) return null;
  return round(tc - hdl - tg / 5, 0);
}

// Ratios
export function ratio(a, b) {
  a = num(a); b = num(b);
  if (!a || !b) return null;
  return round(a / b, 2);
}

// Urine ACR (mg/g) = urine albumin(mg/L) × 100 / urine creatinine(mg/dL)
// Or if same unit (mg/g) already, just alb/creat.
export function urineACR(albumin, creat) {
  albumin = num(albumin); creat = num(creat);
  if (!albumin || !creat) return null;
  return round((albumin * 100) / creat, 1);
}

// Corrected sodium (Katz): Na + 1.6 × ((glucose − 100)/100)
export function correctedNa(na, glucose) {
  na = num(na); glucose = num(glucose);
  if (na === null || glucose === null) return null;
  return round(na + 1.6 * ((glucose - 100) / 100), 1);
}
// Corrected calcium: Ca + 0.8 × (4 − albumin)
export function correctedCa(ca, albumin) {
  ca = num(ca); albumin = num(albumin);
  if (ca === null || albumin === null) return null;
  return round(ca + 0.8 * (4 - albumin), 2);
}

// FIB-4 = age × AST / (platelets(×10⁹/L) × √ALT)
export function fib4(age, ast, platelets, alt) {
  age = num(age); ast = num(ast); platelets = num(platelets); alt = num(alt);
  if (!age || !ast || !platelets || !alt) return null;
  return round((age * ast) / (platelets * Math.sqrt(alt)), 2);
}

// BSA (Mosteller) = √((height cm × weight kg) / 3600)
export function bsaMosteller(heightCm, weightKg) {
  heightCm = num(heightCm); weightKg = num(weightKg);
  if (!heightCm || !weightKg) return null;
  return round(Math.sqrt((heightCm * weightKg) / 3600), 2);
}
// BMI
export function bmi(heightCm, weightKg) {
  heightCm = num(heightCm); weightKg = num(weightKg);
  if (!heightCm || !weightKg) return null;
  const m = heightCm / 100;
  return round(weightKg / (m * m), 1);
}
// WHR = waist / hip
export function whr(waist, hip) {
  waist = num(waist); hip = num(hip);
  if (!waist || !hip) return null;
  return round(waist / hip, 2);
}
// Waist-Height Ratio (WHtR) — central-obesity marker (Asian cutoff 0.5)
export function whtr(waistCm, heightCm) {
  waistCm = num(waistCm); heightCm = num(heightCm);
  if (!waistCm || !heightCm) return null;
  return round(waistCm / heightCm, 2);
}

// --------- NEW (2026-02) Lab-derived calculators ---------

// Cockcroft-Gault CrCl (mL/min) = (140-age) × weight / (72 × Scr) × 0.85 (F)
export function crClCG(scr, age, weightKg, sex) {
  scr = num(scr); age = num(age); weightKg = num(weightKg);
  if (!scr || !age || !weightKg) return null;
  let cl = ((140 - age) * weightKg) / (72 * scr);
  if (sex && String(sex).toUpperCase().startsWith("F")) cl *= 0.85;
  return round(cl, 0);
}

// BUN/Creatinine ratio — pre-renal >20, intrinsic <15
export function bunCrRatio(bun, scr) {
  bun = num(bun); scr = num(scr);
  if (!bun || !scr) return null;
  return round(bun / scr, 1);
}

// Anion Gap = Na - (Cl + HCO3). Normal 8-12.
export function anionGap(na, cl, hco3) {
  na = num(na); cl = num(cl); hco3 = num(hco3);
  if (na === null || cl === null || hco3 === null) return null;
  return round(na - (cl + hco3), 1);
}
// Albumin-corrected AG = AG + 2.5 × (4 - albumin)
export function correctedAG(ag, albumin) {
  ag = num(ag); albumin = num(albumin);
  if (ag === null || albumin === null) return null;
  return round(ag + 2.5 * (4 - albumin), 1);
}

// Serum Osmolality (calc) = 2×Na + Glucose/18 + BUN/2.8
export function serumOsm(na, glucose, bun) {
  na = num(na); glucose = num(glucose); bun = num(bun);
  if (na === null || glucose === null || bun === null) return null;
  return round(2 * na + glucose / 18 + bun / 2.8, 0);
}

// Winter's Formula — expected pCO2 in metabolic acidosis = 1.5 × HCO3 + 8 (± 2)
export function wintersPco2(hco3) {
  hco3 = num(hco3);
  if (hco3 === null) return null;
  return round(1.5 * hco3 + 8, 1);
}

// Fractional Excretion of Sodium (FeNa) = (UNa × SCr) / (SNa × UCr) × 100
// Pre-renal <1%, ATN >2%
export function feNa(uNa, sNa, uCr, sCr) {
  uNa = num(uNa); sNa = num(sNa); uCr = num(uCr); sCr = num(sCr);
  if (!uNa || !sNa || !uCr || !sCr) return null;
  return round((uNa * sCr) / (sNa * uCr) * 100, 2);
}

// Ca × PO4 product (mg²/dL²) — vascular calcification risk when > 55
export function caPhosProduct(ca, phos) {
  ca = num(ca); phos = num(phos);
  if (!ca || !phos) return null;
  return round(ca * phos, 1);
}

// Atherogenic Index of Plasma (AIP) = log10(TG / HDL)
export function aip(tg, hdl) {
  tg = num(tg); hdl = num(hdl);
  if (!tg || !hdl) return null;
  return round(Math.log10(tg / hdl), 2);
}

// Remnant Cholesterol = TC - HDL - LDL (mg/dL). Target < 30.
export function remnantChol(tc, hdl, ldl) {
  tc = num(tc); hdl = num(hdl); ldl = num(ldl);
  if (tc === null || hdl === null || ldl === null) return null;
  return round(tc - hdl - ldl, 0);
}

// TyG Index = ln(TG × FBS / 2), TG & FBS in mg/dL. IR when >4.68.
export function tygIndex(tg, fbs) {
  tg = num(tg); fbs = num(fbs);
  if (!tg || !fbs) return null;
  return round(Math.log(tg * fbs / 2), 2);
}

// METS-IR = ln((2×FBS + TG) × BMI) / ln(HDL). >50 = metabolic risk.
export function metsIR(fbs, tg, bmiVal, hdl) {
  fbs = num(fbs); tg = num(tg); bmiVal = num(bmiVal); hdl = num(hdl);
  if (!fbs || !tg || !bmiVal || !hdl) return null;
  const denom = Math.log(hdl);
  if (!denom) return null;
  return round(Math.log((2 * fbs + tg) * bmiVal) / denom, 2);
}

// QUICKI = 1 / (log10(FBS mg/dL) + log10(Insulin µU/mL)). >0.357 sensitive, <0.331 IR.
export function quicki(fbs, insulin) {
  fbs = num(fbs); insulin = num(insulin);
  if (!fbs || !insulin) return null;
  return round(1 / (Math.log10(fbs) + Math.log10(insulin)), 3);
}

// Neutrophil-Lymphocyte Ratio (NLR). Absolute or % — same ratio.
export function nlr(neut, lymph) {
  neut = num(neut); lymph = num(lymph);
  if (!neut || !lymph) return null;
  return round(neut / lymph, 2);
}
// Platelet-Lymphocyte Ratio (PLR).
export function plr(plt, lymph) {
  plt = num(plt); lymph = num(lymph);
  if (!plt || !lymph) return null;
  return round(plt / lymph, 1);
}
// Systemic Immune-Inflammation Index (SII) = Platelet × Neutrophil / Lymphocyte
// (all absolute counts, per µL). > 500 = high inflammation.
export function sii(plt, neut, lymph) {
  plt = num(plt); neut = num(neut); lymph = num(lymph);
  if (!plt || !neut || !lymph) return null;
  return round(plt * neut / lymph, 0);
}
// Mentzer Index = MCV(fL) / RBC(×10⁶/µL). <13 → thalassemia; >13 → IDA.
export function mentzer(mcv, rbc) {
  mcv = num(mcv); rbc = num(rbc);
  if (!mcv || !rbc) return null;
  return round(mcv / rbc, 1);
}
// Transferrin Saturation (%) = Iron / TIBC × 100. IDA <20%, iron overload >45%.
export function transferrinSat(iron, tibc) {
  iron = num(iron); tibc = num(tibc);
  if (!iron || !tibc) return null;
  return round(iron / tibc * 100, 1);
}

// APRI = (AST / AST_ULN) × 100 / Platelets(×10⁹/L). ULN AST = 40.
// >0.5 → significant fibrosis, >1.5 → cirrhosis.
export function apri(ast, plt) {
  ast = num(ast); plt = num(plt);
  if (!ast || !plt) return null;
  return round((ast / 40) * 100 / plt, 2);
}
// NAFLD Fibrosis Score = -1.675 + 0.037×age + 0.094×BMI + 1.13×IFG/DM +
//                        0.99×(AST/ALT) - 0.013×Plt - 0.66×Albumin
// dmFlag = 1 if diabetic or IFG, else 0.
export function nafldFib(age, bmiVal, ast, alt, plt, alb, dmFlag) {
  age = num(age); bmiVal = num(bmiVal); ast = num(ast); alt = num(alt);
  plt = num(plt); alb = num(alb);
  if (!age || !bmiVal || !ast || !alt || !plt || !alb) return null;
  const dm = dmFlag ? 1 : 0;
  const v = -1.675 + 0.037*age + 0.094*bmiVal + 1.13*dm + 0.99*(ast/alt) - 0.013*plt - 0.66*alb;
  return round(v, 2);
}
export function nafldFibInterp(v) {
  if (v === null || v === undefined) return "";
  if (v < -1.455) return "F0-F2 (no adv fibrosis)";
  if (v <= 0.675) return "Indeterminate";
  return "F3-F4 (advanced fibrosis)";
}

// MELD (original, mg/dL & mg/dL & INR) — clamped per UNOS.
// = 9.57 × ln(Cr) + 3.78 × ln(bilirubin) + 11.20 × ln(INR) + 6.43
export function meld(cr, bili, inr) {
  cr = num(cr); bili = num(bili); inr = num(inr);
  if (!cr || !bili || !inr) return null;
  const c = Math.max(1, Math.min(cr, 4));
  const b = Math.max(1, bili);
  const i = Math.max(1, inr);
  return round(9.57 * Math.log(c) + 3.78 * Math.log(b) + 11.20 * Math.log(i) + 6.43, 0);
}
// MELD-Na = MELD + 1.32 × (137 − Na) − [0.033 × MELD × (137 − Na)]
// Na clamped 125–137.
export function meldNa(meldScore, na) {
  meldScore = num(meldScore); na = num(na);
  if (meldScore === null || na === null) return null;
  const cn = Math.max(125, Math.min(na, 137));
  const v = meldScore + 1.32 * (137 - cn) - (0.033 * meldScore * (137 - cn));
  return round(v, 0);
}
// Maddrey Discriminant Function = 4.6 × (PT_patient - PT_control) + Bilirubin
// PT_control default 12s. >32 → severe alcoholic hepatitis.
export function maddreyDF(ptPatient, bili, ptCtrl = 12) {
  ptPatient = num(ptPatient); bili = num(bili);
  if (!ptPatient || !bili) return null;
  return round(4.6 * (ptPatient - ptCtrl) + bili, 1);
}

// eGFR interpretation
export function ckdStage(egfr) {
  if (egfr === null || egfr === undefined) return "";
  if (egfr >= 90) return "G1 (normal)";
  if (egfr >= 60) return "G2 (mildly ↓)";
  if (egfr >= 45) return "G3a (mild-mod)";
  if (egfr >= 30) return "G3b (mod-severe)";
  if (egfr >= 15) return "G4 (severe)";
  return "G5 (kidney failure)";
}
export function bmiCategory(v) {
  if (v === null || v === undefined) return "";
  if (v < 18.5) return "Underweight";
  if (v < 25) return "Normal";       // For Asian-Indian: normal <23; using WHO for consistency
  if (v < 30) return "Overweight";
  if (v < 35) return "Obesity I";
  if (v < 40) return "Obesity II";
  return "Obesity III";
}
export function fib4Interp(v) {
  if (v === null || v === undefined) return "";
  if (v < 1.3) return "Low risk";
  if (v <= 2.67) return "Indeterminate";
  return "High risk";
}

// -------------------- component --------------------

function findInv(rows, testName) {
  // Return the result of a row whose test_name canonically matches testName.
  const target = canonicalKey(testName);
  if (!target) return "";
  for (const r of rows || []) {
    if (canonicalKey(r.test_name) === target && r.result) return r.result;
  }
  return "";
}

const Field = ({ label, hint, children, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    <div className="flex items-baseline justify-between">
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
      {hint && <span className="text-[9px] text-slate-400 tabular">{hint}</span>}
    </div>
    {children}
  </div>
);

const GroupTitle = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider text-pink-700 font-bold border-b border-pink-100 pb-1 mb-2">{children}</div>
);

const ResultCard = ({ title, value, unit, interp, onPush, onCopyToRx, formula, pinned, onTogglePin }) => {
  const slug = title.replace(/[\s()/×]+/g, "-").toLowerCase().replace(/-+$/g, "");
  return (
  <div className={`border rounded-lg p-3 bg-white hover:shadow-sm transition space-y-1.5 min-h-[92px] flex flex-col ${pinned ? "border-pink-400 ring-1 ring-pink-200" : "border-slate-200 hover:border-pink-200"}`} data-testid={`calc-result-${slug}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-snug flex-1">
        {title}
        {formula && (
          <span className="inline-flex items-center align-middle ml-1" title={formula}>
            <Info size={10} className="text-slate-400" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onTogglePin && (
          <button
            onClick={onTogglePin}
            className={`p-0.5 rounded hover:bg-pink-50 ${pinned ? "text-pink-600" : "text-slate-400"}`}
            title={pinned ? "Unpin from printed prescription" : "Pin to printed prescription"}
            data-testid={`calc-pin-${slug}`}
          >
            {pinned ? <Pin size={11} fill="currentColor" /> : <PinOff size={11} />}
          </button>
        )}
        {onPush && value !== null && value !== undefined && value !== "" && (
          <button onClick={onPush} className="text-[10px] text-pink-700 hover:underline flex items-center gap-0.5" data-testid={`calc-push-${slug}`}>
            <Plus size={10} /> add
          </button>
        )}
        {onCopyToRx && value !== null && value !== undefined && value !== "" && (
          <button
            onClick={onCopyToRx}
            className="text-[10px] text-emerald-700 hover:underline flex items-center gap-0.5"
            title="Insert result + interpretation into Advice/Rx notes"
            data-testid={`calc-rx-${slug}`}
          >
            <FileText size={10} /> Rx
          </button>
        )}
      </div>
    </div>
    <div className="flex items-baseline gap-2 mt-auto">
      <span className="text-xl font-semibold text-slate-900 tabular leading-none">
        {value === null || value === undefined || value === "" ? "—" : value}
      </span>
      {unit && <span className="text-[11px] text-slate-500">{unit}</span>}
    </div>
    {interp && <div className="text-[11px] text-slate-600 leading-tight">{interp}</div>}
  </div>
  );
};

export default function ClinicalCalculators({ investigations, vitals, age, sex, onPushInvestigation, onAppendAdvice }) {
  // Auto-expand the panel when the doctor has at least one lab value entered
  // OR when they arrive from a patient with vitals — so the panel is never
  // "hidden and forgotten". Doctor can still collapse it manually.
  const hasAnyValue = React.useMemo(
    () => (investigations || []).some((r) => r.result && String(r.result).trim() !== ""),
    [investigations]
  );
  const [open, setOpen] = useState(hasAnyValue);
  const wasOpenedByUser = React.useRef(false);
  React.useEffect(() => {
    // If a lab result appears and the user hasn't toggled manually, open it.
    if (hasAnyValue && !wasOpenedByUser.current) setOpen(true);
  }, [hasAnyValue]);
  const [pinned, setPinned] = useState(() => loadPinnedCalcs());
  const [unpinnedKeys, setUnpinnedKeys] = useState(() => loadUnpinnedCalcs());
  const togglePin = (key, title, value, unit, interp, hint) => {
    const wasPinned = pinned.some((p) => p.key === key);
    setPinned((prev) => {
      const entry = { key, title, value, unit, interp: interp || "", hint: hint || "" };
      const next = wasPinned ? prev.filter((p) => p.key !== key) : [...prev, entry];
      savePinnedCalcs(next);
      return next;
    });
    setUnpinnedKeys((prev) => {
      const next = new Set(prev);
      if (wasPinned) next.add(key); else next.delete(key);
      saveUnpinnedCalcs(next);
      return next;
    });
  };
  const isPinned = (key) => pinned.some((p) => p.key === key);

  // Pin every non-null calculator result at once — one-tap "print everything
  // I've calculated". Preserves existing pins and adds any that weren't already
  // pinned. Enriches each entry with the calculator's interpretation + mgmt
  // hint so the Print page can show "Significance" (screen-only, doctor's ref).
  const pinAll = () => {
    const additions = [];
    RESULT_MAP.forEach((r) => {
      if (r.value === null || r.value === undefined || r.value === "") return;
      if (pinned.some((p) => p.key === r.key)) return;
      const interp = r.interp || "";
      const hint = mgmtHint(r.key, r.value);
      additions.push({ key: r.key, title: r.title, value: r.value, unit: r.unit, interp, hint });
    });
    setUnpinnedKeys(() => { const s = new Set(); saveUnpinnedCalcs(s); return s; });
    if (!additions.length) return;
    const next = [...pinned, ...additions];
    savePinnedCalcs(next);
    setPinned(next);
  };
  const unpinAll = () => {
    // Remember every currently-computed key as explicitly unpinned so the
    // auto-pin effect doesn't immediately re-pin them.
    setUnpinnedKeys((prev) => {
      const next = new Set(prev);
      RESULT_MAP.forEach((r) => {
        if (r.value !== null && r.value !== undefined && r.value !== "") next.add(r.key);
      });
      saveUnpinnedCalcs(next);
      return next;
    });
    savePinnedCalcs([]);
    setPinned([]);
  };

  // Compose a print-ready one-liner for the Advice/Rx pane. Combines the
  // metric name, the numeric value + unit, and the interpretation (if any),
  // then a clinically actionable suggestion. Ends with a comma so the doctor
  // can keep dictating after it. Prefix is "• " for consistency with lists.
  const copyToRx = (title, value, unit, interp, mgmtHint) => {
    if (onAppendAdvice === undefined || onAppendAdvice === null) return;
    if (value === null || value === undefined || value === "") return;
    const parts = [`${title} = ${value}${unit ? " " + unit : ""}`];
    if (interp) parts.push(`(${interp})`);
    if (mgmtHint) parts.push(`— ${mgmtHint}`);
    onAppendAdvice("• " + parts.join(" "));
  };
  // Management hints per calculator — evidence-based, patient-actionable.
  // Kept short so they fit on the printed Rx as a "clinical reasoning" trail.
  const mgmtHint = (key, value) => {
    const v = Number(value);
    if (!isFinite(v)) return "";
    switch (key) {
      case "egfr_ckdepi":
      case "egfr_mdrd":
        if (v < 15) return "CKD stage 5 — nephrology + dialysis planning";
        if (v < 30) return "CKD stage 4 — avoid metformin, halve most drugs, nephrology referral";
        if (v < 45) return "CKD stage 3b — halve metformin, avoid NSAIDs, 3-monthly RFT + UACR";
        if (v < 60) return "CKD stage 3a — 6-monthly RFT + UACR, review nephrotoxins";
        if (v < 90) return "CKD stage 2 — annual UACR, control BP + sugar";
        return "eGFR normal — continue annual screening";
      case "homa_ir":
        return v > 2.5 ? "Insulin resistance — start Metformin ± lifestyle, consider TZG + GLP-1 if BMI ≥ 27" : "Insulin sensitivity preserved";
      case "eag":
        return v > 154 ? "eAG > 154 (HbA1c > 7) — intensify glycaemic control" : "eAG target range";
      case "ldl_friedewald":
        if (v > 190) return "Very high LDL — start high-intensity statin (Rosuvastatin 20-40 mg)";
        if (v > 130) return "High LDL — start Atorvastatin 20 mg";
        if (v > 100) return "Borderline — statin if DM/ASCVD risk > 7.5%";
        return "LDL target achieved";
      case "non_hdl":
        return v > 130 ? "Non-HDL > 130 — atherogenic; target Non-HDL < LDL target + 30" : "";
      case "tc_hdl":
        return v > 5 ? "High CV risk ratio — intensify statin" : "";
      case "tg_hdl":
        return v > 3.5 ? "Atherogenic dyslipidemia — small dense LDL; add Fenofibrate 145 mg if TG > 500 or persist" : "";
      case "acr":
        if (v >= 300) return "Macroalbuminuria (A3) — start / uptitrate ACEi/ARB, add SGLT-2i, nephrology referral";
        if (v >= 30) return "Microalbuminuria (A2) — start ACEi/ARB, target BP < 130/80, SGLT-2i if T2DM";
        return "Normoalbuminuria — annual UACR";
      case "corr_na":
        if (v < 130) return "Hyponatremia after glucose correction — evaluate volume status + urgent Na management";
        if (v > 145) return "Hypernatremia after glucose correction — evaluate free-water deficit";
        return "";
      case "corr_ca":
        if (v < 8.5) return "True hypocalcemia — check Vit D + PTH + Mg";
        if (v > 10.5) return "True hypercalcemia — check PTH + Vit D + malignancy screen";
        return "";
      case "fib4":
        if (v > 2.67) return "High risk — hepatology referral, elastography / MR-Elastography";
        if (v > 1.3) return "Indeterminate — Fibroscan / ELF to reclassify";
        return "Low risk — annual reassessment if T2DM";
      case "bmi":
        if (v >= 40) return "Obesity class III — bariatric evaluation";
        if (v >= 32.5) return "Obesity class II — GLP-1 + intensive lifestyle";
        if (v >= 27.5) return "Obesity class I (Asian) — GLP-1 candidate, high-protein diet + resistance training";
        if (v >= 23) return "Overweight (Asian) — lifestyle intervention";
        if (v < 18.5) return "Underweight — nutritional assessment";
        return "Normal BMI";
      case "whr":
        return v > 0.9 ? "Android obesity — high metabolic + CV risk" : "";
      case "homa_b":
        return v < 50 ? "β-cell reserve low — earlier insulin/GLP-1 consideration" : "";
      case "whtr":
        return v >= 0.5 ? "Central obesity — cardiometabolic risk (target < 0.5)" : "";
      case "crcl_cg":
        if (v < 30) return "CrCl < 30 — avoid metformin, halve most drugs, NOAC dose reduction";
        if (v < 60) return "CrCl 30-60 — check drug-specific dose adjustments";
        return "CrCl adequate";
      case "bun_cr":
        if (v > 20) return "Pre-renal azotemia — assess volume status, review diuretics/dehydration";
        if (v < 10) return "Intrinsic renal or low-protein diet";
        return "";
      case "anion_gap":
      case "anion_gap_corr":
        if (v > 12) return "High anion-gap metabolic acidosis — screen DKA (ketones), lactate, uraemia, toxic alcohols (MUDPILES)";
        if (v < 8) return "Low AG — review paraproteinaemia, hypoalbuminaemia, lithium/bromide";
        return "";
      case "serum_osm":
        if (v > 320) return "Hyperosmolar state — hydrate, look for HHS/DI";
        if (v < 275) return "Hypoosmolar state — SIADH, water intoxication";
        return "";
      case "winters_pco2":
        return "Compare with measured pCO₂; if measured > expected → superimposed respiratory acidosis; if lower → superimposed respiratory alkalosis";
      case "fena":
        if (v < 1) return "FeNa < 1% — pre-renal AKI, hydrate & reduce diuretics";
        if (v > 2) return "FeNa > 2% — intrinsic (ATN); avoid nephrotoxins";
        return "";
      case "ca_po4":
        return v > 55 ? "Ca × PO₄ > 55 — vascular calcification risk; control phosphate (binder), review Vit-D dose" : "";
      case "aip":
        if (v >= 0.24) return "AIP ≥ 0.24 — high atherogenic risk; small dense LDL; intensify lifestyle + statin ± fibrate";
        if (v >= 0.11) return "AIP 0.11-0.24 — intermediate risk";
        return "AIP low";
      case "remnant_chol":
        return v > 30 ? "Remnant-C > 30 — residual atherogenic risk; consider high-intensity statin ± icosapent" : "";
      case "tyg":
        return v > 4.68 ? "TyG > 4.68 — insulin resistance / T2DM risk; lifestyle + metformin candidate" : "";
      case "mets_ir":
        return v > 50 ? "METS-IR > 50 — metabolic risk; consider GLP-1 in BMI ≥ 27" : "";
      case "quicki":
        return v < 0.331 ? "QUICKI < 0.331 — insulin resistant" : "";
      case "nlr":
        if (v > 5) return "NLR > 5 — significant systemic inflammation; check sepsis/CV risk";
        if (v > 3) return "NLR 3-5 — mild inflammation";
        return "";
      case "plr":
        return v > 200 ? "PLR > 200 — inflammatory / thrombotic risk" : "";
      case "sii":
        return v > 500 ? "SII > 500 — high systemic immune inflammation" : "";
      case "mentzer":
        if (v < 13) return "Mentzer < 13 — thalassemia trait; order Hb electrophoresis";
        return "Mentzer > 13 — favours iron-deficiency anaemia; check ferritin + iron studies";
      case "tsat":
        if (v < 20) return "TSat < 20% — iron deficiency; check ferritin & start iron therapy";
        if (v > 45) return "TSat > 45% — iron overload; consider haemochromatosis workup";
        return "";
      case "apri":
        if (v > 1.5) return "APRI > 1.5 — likely cirrhosis; hepatology referral + Fibroscan";
        if (v > 0.5) return "APRI > 0.5 — significant fibrosis; further evaluation";
        return "";
      case "nafld_fib":
        if (v > 0.675) return "NFS > 0.675 — advanced (F3-F4) fibrosis; hepatology + Fibroscan";
        if (v > -1.455) return "Indeterminate — Fibroscan/ELF to reclassify";
        return "";
      case "meld":
      case "meld_na":
        if (v >= 25) return "MELD ≥ 25 — high 3-month mortality; urgent transplant workup";
        if (v >= 15) return "MELD 15-24 — transplant benefit; refer hepatology";
        return "MELD < 15 — monitor; treat aetiology";
      case "maddrey":
        return v >= 32 ? "Maddrey ≥ 32 — severe alcoholic hepatitis; consider prednisolone 40 mg × 28d after infection screen" : "";
      default:
        return "";
    }
  };
  // Sugar helper — used inside every ResultCard's onCopyToRx prop.
  const rx = (key, title, value, unit, interp) => () => copyToRx(title, value, unit, interp, mgmtHint(key, value));

  // Auto-populate from state; user can override in local input rows.
  const preset = useMemo(() => ({
    scr: findInv(investigations, "creatinine"),
    fbs: findInv(investigations, "FBS") || vitals?.fasting_sugar || "",
    ppbs: findInv(investigations, "PPBS") || vitals?.pp_sugar || "",
    hba1c: findInv(investigations, "HbA1c"),
    insulin: findInv(investigations, "Fasting Insulin"),
    tc: findInv(investigations, "Total Cholesterol"),
    hdl: findInv(investigations, "HDL"),
    ldl: findInv(investigations, "LDL"),
    tg: findInv(investigations, "Triglycerides"),
    ualb: findInv(investigations, "Urine Albumin"),
    ucr: findInv(investigations, "Urine Creatinine"),
    na: findInv(investigations, "Sodium"),
    ca: findInv(investigations, "Calcium"),
    alb: findInv(investigations, "Albumin"),
    plt: findInv(investigations, "Platelets"),
    ast: findInv(investigations, "AST"),
    alt: findInv(investigations, "ALT"),
    // --- NEW inputs for extended calculators (2026-02) ---
    bun: findInv(investigations, "Blood Urea Nitrogen") || findInv(investigations, "BUN"),
    cl: findInv(investigations, "Chloride"),
    hco3: findInv(investigations, "Bicarbonate"),
    phos: findInv(investigations, "Phosphorus"),
    bili: findInv(investigations, "Bilirubin Total"),
    inr: findInv(investigations, "INR"),
    ptSec: findInv(investigations, "PT"),
    iron: findInv(investigations, "Iron"),
    tibc: findInv(investigations, "TIBC"),
    // Absolute WBC differentials (or %). Store as-typed; NLR/PLR/SII ratios are dimensionless.
    neut: findInv(investigations, "Neutrophils"),
    lymph: findInv(investigations, "Lymphocytes"),
    mcv: findInv(investigations, "MCV"),
    rbc: findInv(investigations, "RBC"),
    uNa: findInv(investigations, "Urine Sodium"),
    height: vitals?.height || "",
    weight: vitals?.weight || "",
    waist: vitals?.waist || "",
    hip: vitals?.hip || "",
  }), [investigations, vitals]);

  const [f, setF] = useState(preset);
  // Track which calculator fields the doctor has manually overridden AND the
  // preset value they overrode against. As soon as the underlying investigation
  // value changes (i.e. new labs come in), the override is dropped and the
  // calculator re-syncs — matching the doctor's mental model that the
  // Investigations table is the source of truth.
  const dirtyRef = React.useRef(new Set());
  const lastPresetRef = React.useRef(preset);
  React.useEffect(() => {
    setF((prev) => {
      const next = { ...prev };
      Object.keys(preset).forEach((k) => {
        const before = lastPresetRef.current[k];
        const now = preset[k];
        // If the upstream value for this specific field changed, unconditionally
        // sync — this is the "labs win" rule that fixes stale overrides.
        if (String(before || "") !== String(now || "")) {
          dirtyRef.current.delete(k);
          next[k] = now || "";
          return;
        }
        // Upstream unchanged — preserve doctor's what-if override.
        if (dirtyRef.current.has(k)) return;
        next[k] = now || "";
      });
      return next;
    });
    lastPresetRef.current = preset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigations, vitals]);

  const upd = (k, v) => {
    if (String(v) === String(preset[k] || "")) dirtyRef.current.delete(k);
    else dirtyRef.current.add(k);
    setF({ ...f, [k]: v });
  };
  // Reset all overrides — pulls the freshest lab values into every field.
  const resetFromLabs = () => {
    dirtyRef.current.clear();
    setF((prev) => {
      const next = { ...prev };
      Object.keys(preset).forEach((k) => { next[k] = preset[k] || ""; });
      return next;
    });
  };

  const results = useMemo(() => {
    const egfrCkdEpi = ckdEpi2021(f.scr, age, sex);
    const egfrMdrd = mdrdIndian(f.scr, age, sex);
    const homaIr = homaIR(f.fbs, f.insulin);
    const homaB = homaBeta(f.fbs, f.insulin);
    const eag = eAG(f.hba1c);
    const ldlF = friedewaldLDL(f.tc, f.hdl, f.tg);
    const nonHdl = (num(f.tc) !== null && num(f.hdl) !== null) ? round(num(f.tc) - num(f.hdl), 0) : null;
    const tcHdl = ratio(f.tc, f.hdl);
    const ldlHdl = ratio(f.ldl, f.hdl);
    const tgHdl = ratio(f.tg, f.hdl);
    const acr = urineACR(f.ualb, f.ucr);
    const naCorr = correctedNa(f.na, f.fbs);
    const caCorr = correctedCa(f.ca, f.alb);
    const fib = fib4(age, f.ast, f.plt, f.alt);
    const bmiVal = bmi(f.height, f.weight);
    const bsa = bsaMosteller(f.height, f.weight);
    const whrVal = whr(f.waist, f.hip);
    // --- NEW ---
    const whtrVal = whtr(f.waist, f.height);
    const crcl = crClCG(f.scr, age, f.weight, sex);
    const bunCr = bunCrRatio(f.bun, f.scr);
    const ag = anionGap(f.na, f.cl, f.hco3);
    const agCorr = correctedAG(ag, f.alb);
    const osm = serumOsm(f.na, f.fbs, f.bun);
    const winter = wintersPco2(f.hco3);
    const fena = feNa(f.uNa, f.na, f.ucr, f.scr);
    const caPo4 = caPhosProduct(f.ca, f.phos);
    const aipVal = aip(f.tg, f.hdl);
    const remnant = remnantChol(f.tc, f.hdl, f.ldl);
    const tyg = tygIndex(f.tg, f.fbs);
    const metsIrVal = metsIR(f.fbs, f.tg, bmiVal, f.hdl);
    const quickiVal = quicki(f.fbs, f.insulin);
    const nlrVal = nlr(f.neut, f.lymph);
    const plrVal = plr(f.plt, f.lymph);
    const siiVal = sii(f.plt, f.neut, f.lymph);
    const mentzerVal = mentzer(f.mcv, f.rbc);
    const tsat = transferrinSat(f.iron, f.tibc);
    const apriVal = apri(f.ast, f.plt);
    // NAFLD Fibrosis assumes diabetic/IFG if HbA1c >= 5.7 OR FBS >= 100.
    const dmFlag = (num(f.hba1c) !== null && num(f.hba1c) >= 5.7) || (num(f.fbs) !== null && num(f.fbs) >= 100);
    const nafld = nafldFib(age, bmiVal, f.ast, f.alt, f.plt, f.alb, dmFlag);
    const meldVal = meld(f.scr, f.bili, f.inr);
    const meldNaVal = meldNa(meldVal, f.na);
    const maddrey = maddreyDF(f.ptSec, f.bili);
    return {
      egfrCkdEpi, egfrMdrd, homaIr, homaB, eag, ldlF, nonHdl, tcHdl, ldlHdl, tgHdl,
      acr, naCorr, caCorr, fib, bmiVal, bsa, whrVal,
      whtrVal, crcl, bunCr, ag, agCorr, osm, winter, fena, caPo4,
      aipVal, remnant, tyg, metsIrVal, quickiVal,
      nlrVal, plrVal, siiVal, mentzerVal, tsat,
      apriVal, nafld, meldVal, meldNaVal, maddrey,
    };
  }, [f, age, sex]);

  const push = (name, value, unit) => {
    if (value === null || value === undefined || value === "") return;
    onPushInvestigation({
      test_name: name,
      result: String(value),
      unit,
      reference_range: "",
      test_date: new Date().toISOString().slice(0, 10),
    });
  };

  // Whenever underlying inputs change, refresh the value stored on any pinned
  // entry (so the print always reflects the current calculation).
  const RESULT_MAP = [
    { key: "egfr_ckdepi", title: "eGFR CKD-EPI", value: results.egfrCkdEpi, unit: "mL/min/1.73m²" },
    { key: "egfr_mdrd", title: "eGFR MDRD (Indian)", value: results.egfrMdrd, unit: "mL/min/1.73m²" },
    { key: "homa_ir", title: "HOMA-IR", value: results.homaIr, unit: "" },
    { key: "homa_b", title: "HOMA-β", value: results.homaB, unit: "%" },
    { key: "eag", title: "eAG", value: results.eag, unit: "mg/dL" },
    { key: "ldl_friedewald", title: "LDL (Friedewald)", value: results.ldlF, unit: "mg/dL" },
    { key: "non_hdl", title: "Non-HDL", value: results.nonHdl, unit: "mg/dL" },
    { key: "tc_hdl", title: "TC/HDL", value: results.tcHdl, unit: "" },
    { key: "ldl_hdl", title: "LDL/HDL", value: results.ldlHdl, unit: "" },
    { key: "tg_hdl", title: "TG/HDL", value: results.tgHdl, unit: "" },
    { key: "acr", title: "Urine ACR", value: results.acr, unit: "mg/g" },
    { key: "corr_na", title: "Corrected Na", value: results.naCorr, unit: "mEq/L" },
    { key: "corr_ca", title: "Corrected Ca", value: results.caCorr, unit: "mg/dL" },
    { key: "fib4", title: "FIB-4", value: results.fib, unit: "" },
    { key: "bmi", title: "BMI", value: results.bmiVal, unit: "kg/m²" },
    { key: "bsa", title: "BSA (Mosteller)", value: results.bsa, unit: "m²" },
    { key: "whr", title: "WHR", value: results.whrVal, unit: "" },
    // NEW
    { key: "whtr", title: "Waist-Height", value: results.whtrVal, unit: "" },
    { key: "crcl_cg", title: "CrCl (Cockcroft-Gault)", value: results.crcl, unit: "mL/min" },
    { key: "bun_cr", title: "BUN/Cr Ratio", value: results.bunCr, unit: "" },
    { key: "anion_gap", title: "Anion Gap", value: results.ag, unit: "mEq/L" },
    { key: "anion_gap_corr", title: "Anion Gap (Alb-corrected)", value: results.agCorr, unit: "mEq/L" },
    { key: "serum_osm", title: "Serum Osmolality (calc)", value: results.osm, unit: "mOsm/kg" },
    { key: "winters_pco2", title: "Winter's pCO₂", value: results.winter, unit: "mmHg" },
    { key: "fena", title: "FeNa", value: results.fena, unit: "%" },
    { key: "ca_po4", title: "Ca × PO₄ Product", value: results.caPo4, unit: "mg²/dL²" },
    { key: "aip", title: "AIP", value: results.aipVal, unit: "" },
    { key: "remnant_chol", title: "Remnant Cholesterol", value: results.remnant, unit: "mg/dL" },
    { key: "tyg", title: "TyG Index", value: results.tyg, unit: "" },
    { key: "mets_ir", title: "METS-IR", value: results.metsIrVal, unit: "" },
    { key: "quicki", title: "QUICKI", value: results.quickiVal, unit: "" },
    { key: "nlr", title: "NLR", value: results.nlrVal, unit: "" },
    { key: "plr", title: "PLR", value: results.plrVal, unit: "" },
    { key: "sii", title: "SII", value: results.siiVal, unit: "" },
    { key: "mentzer", title: "Mentzer Index", value: results.mentzerVal, unit: "" },
    { key: "tsat", title: "Transferrin Sat", value: results.tsat, unit: "%" },
    { key: "apri", title: "APRI", value: results.apriVal, unit: "" },
    { key: "nafld_fib", title: "NAFLD Fibrosis Score", value: results.nafld, unit: "" },
    { key: "meld", title: "MELD", value: results.meldVal, unit: "" },
    { key: "meld_na", title: "MELD-Na", value: results.meldNaVal, unit: "" },
    { key: "maddrey", title: "Maddrey DF", value: results.maddrey, unit: "" },
  ];
  React.useEffect(() => {
    // 1) refresh values on existing pins; 2) AUTO-PIN every newly computed
    // result unless the doctor explicitly unpinned it (unpinnedKeys).
    setPinned((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        const r = RESULT_MAP.find((x) => x.key === p.key);
        if (r && (r.value !== p.value || r.unit !== p.unit)) {
          changed = true;
          return { ...p, title: r.title, value: r.value, unit: r.unit, hint: mgmtHint(r.key, r.value) || p.hint || "" };
        }
        return p;
      });
      RESULT_MAP.forEach((r) => {
        if (r.value === null || r.value === undefined || r.value === "") return;
        if (next.some((p) => p.key === r.key)) return;
        if (unpinnedKeys.has(r.key)) return;
        next.push({ key: r.key, title: r.title, value: r.value, unit: r.unit, interp: "", hint: mgmtHint(r.key, r.value) });
        changed = true;
      });
      if (!changed) return prev;
      savePinnedCalcs(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, unpinnedKeys]);

  // Interpretation helpers for the new calculators (kept close to render).
  const _bunCrInterp = results.bunCr !== null ? (results.bunCr > 20 ? "Pre-renal" : results.bunCr < 10 ? "Intrinsic / low-protein" : "Normal") : "";
  const _agInterp = results.ag !== null ? (results.ag > 12 ? "High AG acidosis" : results.ag < 8 ? "Low AG" : "Normal") : "";
  const _agCorrInterp = results.agCorr !== null ? (results.agCorr > 12 ? "High AG acidosis (corrected)" : "Normal (corrected)") : "";
  const _osmInterp = results.osm !== null ? (results.osm > 320 ? "Hyperosmolar" : results.osm < 275 ? "Hypoosmolar" : "Normal") : "";
  const _fenaInterp = results.fena !== null ? (results.fena < 1 ? "Pre-renal AKI" : results.fena > 2 ? "Intrinsic (ATN)" : "Indeterminate") : "";
  const _caPo4Interp = results.caPo4 !== null && results.caPo4 > 55 ? "Vascular calcification risk" : "";
  const _aipInterp = results.aipVal !== null ? (results.aipVal >= 0.24 ? "High CV risk" : results.aipVal >= 0.11 ? "Intermediate" : "Low") : "";
  const _remnantInterp = results.remnant !== null && results.remnant > 30 ? "Residual atherogenic risk" : "";
  const _tygInterp = results.tyg !== null && results.tyg > 4.68 ? "Insulin resistant" : "";
  const _metsIrInterp = results.metsIrVal !== null && results.metsIrVal > 50 ? "Metabolic risk" : "";
  const _quickiInterp = results.quickiVal !== null ? (results.quickiVal < 0.331 ? "Insulin resistant" : results.quickiVal > 0.357 ? "Sensitive" : "Borderline") : "";
  const _nlrInterp = results.nlrVal !== null ? (results.nlrVal > 5 ? "High inflammation" : results.nlrVal > 3 ? "Mild inflammation" : "Normal") : "";
  const _plrInterp = results.plrVal !== null && results.plrVal > 200 ? "Elevated" : "";
  const _siiInterp = results.siiVal !== null && results.siiVal > 500 ? "High inflammation" : "";
  const _mentzerInterp = results.mentzerVal !== null ? (results.mentzerVal < 13 ? "Suggests thalassemia" : "Suggests IDA") : "";
  const _tsatInterp = results.tsat !== null ? (results.tsat < 20 ? "Iron deficiency" : results.tsat > 45 ? "Iron overload" : "Normal") : "";
  const _apriInterp = results.apriVal !== null ? (results.apriVal > 1.5 ? "Cirrhosis likely" : results.apriVal > 0.5 ? "Significant fibrosis" : "Low") : "";
  const _nafldInterp = nafldFibInterp(results.nafld);
  const _meldInterp = results.meldVal !== null ? (results.meldVal >= 25 ? "Very high mortality" : results.meldVal >= 15 ? "Transplant benefit" : "Low") : "";
  const _maddreyInterp = results.maddrey !== null && results.maddrey >= 32 ? "Severe alc. hepatitis" : "";

  const NEW_RESULT_CARDS = [
    { key: "whtr", title: "Waist-Height Ratio", value: results.whtrVal, unit: "", interp: results.whtrVal !== null && results.whtrVal >= 0.5 ? "Central obesity" : "", formula: "Waist / Height (target < 0.5)" },
    { key: "crcl_cg", title: "CrCl (Cockcroft-Gault)", value: results.crcl, unit: "mL/min", interp: results.crcl !== null ? ckdStage(results.crcl) : "", formula: "((140-age) × wt) / (72 × Scr) × 0.85(F)" },
    { key: "bun_cr", title: "BUN/Cr Ratio", value: results.bunCr, unit: "", interp: _bunCrInterp, formula: "BUN / Serum Creatinine" },
    { key: "anion_gap", title: "Anion Gap", value: results.ag, unit: "mEq/L", interp: _agInterp, formula: "Na − (Cl + HCO₃)" },
    { key: "anion_gap_corr", title: "Anion Gap (Alb-corrected)", value: results.agCorr, unit: "mEq/L", interp: _agCorrInterp, formula: "AG + 2.5 × (4 − Albumin)" },
    { key: "serum_osm", title: "Serum Osmolality (calc)", value: results.osm, unit: "mOsm/kg", interp: _osmInterp, formula: "2×Na + Glucose/18 + BUN/2.8" },
    { key: "winters_pco2", title: "Winter's expected pCO₂", value: results.winter, unit: "mmHg", interp: results.winter !== null ? "Compare with measured pCO₂" : "", formula: "1.5 × HCO₃ + 8 (± 2)" },
    { key: "fena", title: "FeNa", value: results.fena, unit: "%", interp: _fenaInterp, formula: "(UNa × SCr) / (SNa × UCr) × 100" },
    { key: "ca_po4", title: "Ca × PO₄ Product", value: results.caPo4, unit: "mg²/dL²", interp: _caPo4Interp, formula: "Calcium × Phosphorus (target < 55)" },
    { key: "aip", title: "Atherogenic Index of Plasma", value: results.aipVal, unit: "", interp: _aipInterp, formula: "log₁₀(TG / HDL)" },
    { key: "remnant_chol", title: "Remnant Cholesterol", value: results.remnant, unit: "mg/dL", interp: _remnantInterp, formula: "TC − HDL − LDL (target < 30)" },
    { key: "tyg", title: "TyG Index", value: results.tyg, unit: "", interp: _tygInterp, formula: "ln(TG × FBS / 2)" },
    { key: "mets_ir", title: "METS-IR", value: results.metsIrVal, unit: "", interp: _metsIrInterp, formula: "ln((2×FBS+TG)×BMI) / ln(HDL)" },
    { key: "quicki", title: "QUICKI", value: results.quickiVal, unit: "", interp: _quickiInterp, formula: "1 / (log₁₀ FBS + log₁₀ Insulin)" },
    { key: "nlr", title: "Neutrophil-Lymphocyte Ratio", value: results.nlrVal, unit: "", interp: _nlrInterp, formula: "Neutrophils / Lymphocytes" },
    { key: "plr", title: "Platelet-Lymphocyte Ratio", value: results.plrVal, unit: "", interp: _plrInterp, formula: "Platelets / Lymphocytes" },
    { key: "sii", title: "Systemic Immune-Inflammation Index", value: results.siiVal, unit: "", interp: _siiInterp, formula: "Platelet × Neutrophil / Lymphocyte" },
    { key: "mentzer", title: "Mentzer Index", value: results.mentzerVal, unit: "", interp: _mentzerInterp, formula: "MCV / RBC (× 10⁶/µL)" },
    { key: "tsat", title: "Transferrin Saturation", value: results.tsat, unit: "%", interp: _tsatInterp, formula: "Iron / TIBC × 100" },
    { key: "apri", title: "APRI (fibrosis)", value: results.apriVal, unit: "", interp: _apriInterp, formula: "(AST / 40) × 100 / Platelets" },
    { key: "nafld_fib", title: "NAFLD Fibrosis Score", value: results.nafld, unit: "", interp: _nafldInterp, formula: "-1.675 + 0.037·age + 0.094·BMI + 1.13·IFG/DM + 0.99·(AST/ALT) - 0.013·Plt - 0.66·Alb" },
    { key: "meld", title: "MELD Score", value: results.meldVal, unit: "", interp: _meldInterp, formula: "9.57·ln(Cr) + 3.78·ln(Bili) + 11.20·ln(INR) + 6.43" },
    { key: "meld_na", title: "MELD-Na Score", value: results.meldNaVal, unit: "", interp: _meldInterp, formula: "MELD + 1.32·(137−Na) − 0.033·MELD·(137−Na)" },
    { key: "maddrey", title: "Maddrey DF (alcoholic hepatitis)", value: results.maddrey, unit: "", interp: _maddreyInterp, formula: "4.6 × (PT − 12) + Bilirubin" },
  ];

  return (
    <div className="mt-3 border border-slate-200 rounded-md" data-testid="calculators-panel">
      <button
        onClick={() => { wasOpenedByUser.current = true; setOpen(!open); }}
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50"
        data-testid="calculators-toggle"
      >
        <span className="flex items-center gap-2 font-medium text-slate-700">
          <Calculator size={14} className="text-pink-600" /> Clinical calculators
          <span className="text-[10px] text-slate-500 font-normal">eGFR · CrCl · HOMA · TyG · AIP · MELD · APRI · NAFLD · Anion Gap · NLR · Mentzer</span>
          {!hasAnyValue && (
            <span className="text-[9px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 ml-1 font-semibold">Enter lab results to compute</span>
          )}
        </span>
        {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
      </button>

      {open && (
        <div className="p-5 border-t border-slate-100 space-y-6">
          {/* Inputs grouped by category — labels sit above inputs, generous spacing */}
          <div className="space-y-5">
            <div>
              <GroupTitle>Renal &amp; Glycaemic</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Creatinine" hint="mg/dL"><Input value={f.scr} onChange={(e) => upd("scr", e.target.value)} className="h-9 text-sm tabular" placeholder="1.0" data-testid="calc-scr" /></Field>
                <Field label="FBS" hint="mg/dL"><Input value={f.fbs} onChange={(e) => upd("fbs", e.target.value)} className="h-9 text-sm tabular" placeholder="120" data-testid="calc-fbs" /></Field>
                <Field label="Fasting insulin" hint="µU/mL"><Input value={f.insulin} onChange={(e) => upd("insulin", e.target.value)} className="h-9 text-sm tabular" placeholder="10" data-testid="calc-insulin" /></Field>
                <Field label="HbA1c" hint="%"><Input value={f.hba1c} onChange={(e) => upd("hba1c", e.target.value)} className="h-9 text-sm tabular" placeholder="7.0" data-testid="calc-hba1c" /></Field>
              </div>
            </div>

            <div>
              <GroupTitle>Lipids</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Total cholesterol" hint="mg/dL"><Input value={f.tc} onChange={(e) => upd("tc", e.target.value)} className="h-9 text-sm tabular" placeholder="180" data-testid="calc-tc" /></Field>
                <Field label="HDL" hint="mg/dL"><Input value={f.hdl} onChange={(e) => upd("hdl", e.target.value)} className="h-9 text-sm tabular" placeholder="45" data-testid="calc-hdl" /></Field>
                <Field label="LDL" hint="mg/dL"><Input value={f.ldl} onChange={(e) => upd("ldl", e.target.value)} className="h-9 text-sm tabular" placeholder="100" data-testid="calc-ldl" /></Field>
                <Field label="Triglycerides" hint="mg/dL"><Input value={f.tg} onChange={(e) => upd("tg", e.target.value)} className="h-9 text-sm tabular" placeholder="150" data-testid="calc-tg" /></Field>
              </div>
            </div>

            <div>
              <GroupTitle>Urine &amp; Electrolytes</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Urine albumin" hint="mg/L"><Input value={f.ualb} onChange={(e) => upd("ualb", e.target.value)} className="h-9 text-sm tabular" placeholder="30" data-testid="calc-ualb" /></Field>
                <Field label="Urine creatinine" hint="mg/dL"><Input value={f.ucr} onChange={(e) => upd("ucr", e.target.value)} className="h-9 text-sm tabular" placeholder="100" data-testid="calc-ucr" /></Field>
                <Field label="Sodium" hint="mEq/L"><Input value={f.na} onChange={(e) => upd("na", e.target.value)} className="h-9 text-sm tabular" placeholder="140" data-testid="calc-na" /></Field>
                <Field label="Calcium" hint="mg/dL"><Input value={f.ca} onChange={(e) => upd("ca", e.target.value)} className="h-9 text-sm tabular" placeholder="9.5" data-testid="calc-ca" /></Field>
              </div>
            </div>

            <div>
              <GroupTitle>Hepatic &amp; Anthropometry</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Albumin" hint="g/dL"><Input value={f.alb} onChange={(e) => upd("alb", e.target.value)} className="h-9 text-sm tabular" placeholder="4.0" data-testid="calc-alb" /></Field>
                <Field label="Platelets" hint="×10⁹/L"><Input value={f.plt} onChange={(e) => upd("plt", e.target.value)} className="h-9 text-sm tabular" placeholder="250" data-testid="calc-plt" /></Field>
                <Field label="AST" hint="U/L"><Input value={f.ast} onChange={(e) => upd("ast", e.target.value)} className="h-9 text-sm tabular" placeholder="25" data-testid="calc-ast" /></Field>
                <Field label="ALT" hint="U/L"><Input value={f.alt} onChange={(e) => upd("alt", e.target.value)} className="h-9 text-sm tabular" placeholder="25" data-testid="calc-alt" /></Field>
              </div>
            </div>

            <div>
              <GroupTitle>Extended Chemistry (fluid / AG / MELD)</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="BUN" hint="mg/dL"><Input value={f.bun} onChange={(e) => upd("bun", e.target.value)} className="h-9 text-sm tabular" placeholder="14" data-testid="calc-bun" /></Field>
                <Field label="Chloride" hint="mEq/L"><Input value={f.cl} onChange={(e) => upd("cl", e.target.value)} className="h-9 text-sm tabular" placeholder="100" data-testid="calc-cl" /></Field>
                <Field label="Bicarbonate" hint="mEq/L"><Input value={f.hco3} onChange={(e) => upd("hco3", e.target.value)} className="h-9 text-sm tabular" placeholder="24" data-testid="calc-hco3" /></Field>
                <Field label="Phosphorus" hint="mg/dL"><Input value={f.phos} onChange={(e) => upd("phos", e.target.value)} className="h-9 text-sm tabular" placeholder="3.5" data-testid="calc-phos" /></Field>
                <Field label="Bilirubin (total)" hint="mg/dL"><Input value={f.bili} onChange={(e) => upd("bili", e.target.value)} className="h-9 text-sm tabular" placeholder="1.0" data-testid="calc-bili" /></Field>
                <Field label="INR"><Input value={f.inr} onChange={(e) => upd("inr", e.target.value)} className="h-9 text-sm tabular" placeholder="1.0" data-testid="calc-inr" /></Field>
                <Field label="PT (sec)" hint="control ≈ 12"><Input value={f.ptSec} onChange={(e) => upd("ptSec", e.target.value)} className="h-9 text-sm tabular" placeholder="14" data-testid="calc-pt" /></Field>
                <Field label="Urine sodium" hint="mEq/L"><Input value={f.uNa} onChange={(e) => upd("uNa", e.target.value)} className="h-9 text-sm tabular" placeholder="40" data-testid="calc-una" /></Field>
              </div>
            </div>

            <div>
              <GroupTitle>Haematology &amp; Iron studies</GroupTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Neutrophils" hint="abs or %"><Input value={f.neut} onChange={(e) => upd("neut", e.target.value)} className="h-9 text-sm tabular" placeholder="60" data-testid="calc-neut" /></Field>
                <Field label="Lymphocytes" hint="abs or %"><Input value={f.lymph} onChange={(e) => upd("lymph", e.target.value)} className="h-9 text-sm tabular" placeholder="30" data-testid="calc-lymph" /></Field>
                <Field label="MCV" hint="fL"><Input value={f.mcv} onChange={(e) => upd("mcv", e.target.value)} className="h-9 text-sm tabular" placeholder="85" data-testid="calc-mcv" /></Field>
                <Field label="RBC" hint="×10⁶/µL"><Input value={f.rbc} onChange={(e) => upd("rbc", e.target.value)} className="h-9 text-sm tabular" placeholder="4.7" data-testid="calc-rbc" /></Field>
                <Field label="Serum Iron" hint="µg/dL"><Input value={f.iron} onChange={(e) => upd("iron", e.target.value)} className="h-9 text-sm tabular" placeholder="80" data-testid="calc-iron" /></Field>
                <Field label="TIBC" hint="µg/dL"><Input value={f.tibc} onChange={(e) => upd("tibc", e.target.value)} className="h-9 text-sm tabular" placeholder="350" data-testid="calc-tibc" /></Field>
              </div>
            </div>
          </div>

          {/* Results — 2 columns on tablet, 3 on desktop, each card gets breathing room */}
          <div>
            <div className="flex items-center justify-between gap-2 border-b border-pink-100 pb-1 mb-2">
              <div className="text-[10px] uppercase tracking-wider text-pink-700 font-bold">Calculated results</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 tabular" data-testid="calc-pinned-count">
                  {pinned.length} pinned for Rx print
                </span>
                <button
                  type="button"
                  onClick={pinAll}
                  className="text-[10px] px-2 py-0.5 rounded border border-pink-300 bg-pink-50 text-pink-700 hover:bg-pink-100 inline-flex items-center gap-1"
                  data-testid="calc-pin-all"
                  title="Pin every non-null calculator result so it prints on the prescription"
                >
                  <Pin size={10} fill="currentColor" /> Pin all
                </button>
                {pinned.length > 0 && (
                  <button
                    type="button"
                    onClick={unpinAll}
                    className="text-[10px] px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
                    data-testid="calc-unpin-all"
                    title="Clear all pinned calculators"
                  >
                    <PinOff size={10} /> Clear
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <ResultCard
                title="eGFR CKD-EPI 2021"
                value={results.egfrCkdEpi}
                unit="mL/min/1.73m²"
                interp={ckdStage(results.egfrCkdEpi)}
                formula="142 × min(Scr/κ,1)^α × max(Scr/κ,1)^-1.200 × 0.9938^age × 1.012 (F). κ=0.7/0.9, α=-0.241/-0.302."
                onPush={() => push("eGFR (CKD-EPI 2021)", results.egfrCkdEpi, "mL/min/1.73m²")}
                onCopyToRx={rx("egfr_ckdepi", "eGFR (CKD-EPI 2021)", results.egfrCkdEpi, "mL/min/1.73m²", ckdStage(results.egfrCkdEpi))}
                pinned={isPinned("egfr_ckdepi")}
                onTogglePin={() => togglePin("egfr_ckdepi", RESULT_MAP.find(x => x.key === "egfr_ckdepi").title, RESULT_MAP.find(x => x.key === "egfr_ckdepi").value, RESULT_MAP.find(x => x.key === "egfr_ckdepi").unit)}
              />
              <ResultCard
                title="eGFR MDRD (Indian ×1.212)"
                value={results.egfrMdrd}
                unit="mL/min/1.73m²"
                interp={ckdStage(results.egfrMdrd)}
                formula="175 × Scr^-1.154 × Age^-0.203 × 1.212 × 0.742 (if F) — Kher 2005"
                onPush={() => push("eGFR (MDRD Indian)", results.egfrMdrd, "mL/min/1.73m²")}
                onCopyToRx={rx("egfr_mdrd", "eGFR (MDRD Indian)", results.egfrMdrd, "mL/min/1.73m²", ckdStage(results.egfrMdrd))}
                pinned={isPinned("egfr_mdrd")}
                onTogglePin={() => togglePin("egfr_mdrd", RESULT_MAP.find(x => x.key === "egfr_mdrd").title, RESULT_MAP.find(x => x.key === "egfr_mdrd").value, RESULT_MAP.find(x => x.key === "egfr_mdrd").unit)}
              />
              <ResultCard
                title="HOMA-IR"
                value={results.homaIr}
                interp={results.homaIr !== null ? (results.homaIr > 2.5 ? "Insulin resistant" : "Normal") : ""}
                formula="FBS(mg/dL) × Fasting Insulin(µU/mL) / 405"
                onPush={() => push("HOMA-IR", results.homaIr, "")}
                onCopyToRx={rx("homa_ir", "HOMA-IR", results.homaIr, "", results.homaIr !== null ? (results.homaIr > 2.5 ? "Insulin resistant" : "Normal") : "")}
                pinned={isPinned("homa_ir")}
                onTogglePin={() => togglePin("homa_ir", RESULT_MAP.find(x => x.key === "homa_ir").title, RESULT_MAP.find(x => x.key === "homa_ir").value, RESULT_MAP.find(x => x.key === "homa_ir").unit)}
              />
              <ResultCard title="HOMA-β" value={results.homaB} unit="%" formula="20 × Insulin / (FBS(mmol/L) − 3.5). mmol = mg/dL ÷ 18" onPush={() => push("HOMA-β", results.homaB, "%")}
                onCopyToRx={rx("homa_b", "HOMA-β", results.homaB, "%", "")}
                pinned={isPinned("homa_b")}
                onTogglePin={() => togglePin("homa_b", RESULT_MAP.find(x => x.key === "homa_b").title, RESULT_MAP.find(x => x.key === "homa_b").value, RESULT_MAP.find(x => x.key === "homa_b").unit)} />
              <ResultCard title="eAG (from HbA1c)" value={results.eag} unit="mg/dL" formula="28.7 × HbA1c − 46.7" onPush={() => push("eAG", results.eag, "mg/dL")}
                onCopyToRx={rx("eag", "eAG", results.eag, "mg/dL", "")}
                pinned={isPinned("eag")}
                onTogglePin={() => togglePin("eag", RESULT_MAP.find(x => x.key === "eag").title, RESULT_MAP.find(x => x.key === "eag").value, RESULT_MAP.find(x => x.key === "eag").unit)} />
              <ResultCard title="Friedewald LDL" value={results.ldlF} unit="mg/dL" interp={results.ldlF === null ? "TG ≥ 400 – use direct LDL" : ""} formula="TC − HDL − TG/5 (valid TG < 400)" onPush={() => push("LDL (Friedewald)", results.ldlF, "mg/dL")}
                onCopyToRx={rx("ldl_friedewald", "LDL (Friedewald)", results.ldlF, "mg/dL", "")}
                pinned={isPinned("ldl_friedewald")}
                onTogglePin={() => togglePin("ldl_friedewald", RESULT_MAP.find(x => x.key === "ldl_friedewald").title, RESULT_MAP.find(x => x.key === "ldl_friedewald").value, RESULT_MAP.find(x => x.key === "ldl_friedewald").unit)} />
              <ResultCard title="Non-HDL cholesterol" value={results.nonHdl} unit="mg/dL" formula="TC − HDL" onPush={() => push("Non-HDL", results.nonHdl, "mg/dL")}
                onCopyToRx={rx("non_hdl", "Non-HDL", results.nonHdl, "mg/dL", "")}
                pinned={isPinned("non_hdl")}
                onTogglePin={() => togglePin("non_hdl", RESULT_MAP.find(x => x.key === "non_hdl").title, RESULT_MAP.find(x => x.key === "non_hdl").value, RESULT_MAP.find(x => x.key === "non_hdl").unit)} />
              <ResultCard title="TC / HDL ratio" value={results.tcHdl} interp={results.tcHdl !== null ? (results.tcHdl > 5 ? "High CV risk" : results.tcHdl > 3.5 ? "Borderline" : "Desirable") : ""} formula="Total cholesterol / HDL" onPush={() => push("TC/HDL ratio", results.tcHdl, "")}
                onCopyToRx={rx("tc_hdl", "TC/HDL ratio", results.tcHdl, "", results.tcHdl !== null ? (results.tcHdl > 5 ? "High CV risk" : results.tcHdl > 3.5 ? "Borderline" : "Desirable") : "")}
                pinned={isPinned("tc_hdl")}
                onTogglePin={() => togglePin("tc_hdl", RESULT_MAP.find(x => x.key === "tc_hdl").title, RESULT_MAP.find(x => x.key === "tc_hdl").value, RESULT_MAP.find(x => x.key === "tc_hdl").unit)} />
              <ResultCard title="LDL / HDL ratio" value={results.ldlHdl} formula="LDL / HDL" onPush={() => push("LDL/HDL ratio", results.ldlHdl, "")}
                onCopyToRx={rx("ldl_hdl", "LDL/HDL ratio", results.ldlHdl, "", "")}
                pinned={isPinned("ldl_hdl")}
                onTogglePin={() => togglePin("ldl_hdl", RESULT_MAP.find(x => x.key === "ldl_hdl").title, RESULT_MAP.find(x => x.key === "ldl_hdl").value, RESULT_MAP.find(x => x.key === "ldl_hdl").unit)} />
              <ResultCard title="TG / HDL ratio" value={results.tgHdl} interp={results.tgHdl !== null ? (results.tgHdl > 3.5 ? "Atherogenic" : "OK") : ""} formula="TG / HDL — insulin-resistance surrogate" onPush={() => push("TG/HDL ratio", results.tgHdl, "")}
                onCopyToRx={rx("tg_hdl", "TG/HDL ratio", results.tgHdl, "", results.tgHdl !== null ? (results.tgHdl > 3.5 ? "Atherogenic" : "OK") : "")}
                pinned={isPinned("tg_hdl")}
                onTogglePin={() => togglePin("tg_hdl", RESULT_MAP.find(x => x.key === "tg_hdl").title, RESULT_MAP.find(x => x.key === "tg_hdl").value, RESULT_MAP.find(x => x.key === "tg_hdl").unit)} />
              <ResultCard title="Urine ACR" value={results.acr} unit="mg/g" interp={results.acr !== null ? (results.acr < 30 ? "Normoalbuminuria" : results.acr < 300 ? "Microalbuminuria" : "Macroalbuminuria") : ""} formula="Urine alb (mg/L) × 100 / Urine creat (mg/dL)" onPush={() => push("Urine ACR", results.acr, "mg/g")}
                onCopyToRx={rx("acr", "Urine ACR", results.acr, "mg/g", results.acr !== null ? (results.acr < 30 ? "Normoalbuminuria" : results.acr < 300 ? "Microalbuminuria" : "Macroalbuminuria") : "")}
                pinned={isPinned("acr")}
                onTogglePin={() => togglePin("acr", RESULT_MAP.find(x => x.key === "acr").title, RESULT_MAP.find(x => x.key === "acr").value, RESULT_MAP.find(x => x.key === "acr").unit)} />
              <ResultCard title="Corrected sodium" value={results.naCorr} unit="mEq/L" formula="Na + 1.6 × ((Glucose − 100)/100)" onPush={() => push("Corrected Sodium", results.naCorr, "mEq/L")}
                onCopyToRx={rx("corr_na", "Corrected Sodium", results.naCorr, "mEq/L", "")}
                pinned={isPinned("corr_na")}
                onTogglePin={() => togglePin("corr_na", RESULT_MAP.find(x => x.key === "corr_na").title, RESULT_MAP.find(x => x.key === "corr_na").value, RESULT_MAP.find(x => x.key === "corr_na").unit)} />
              <ResultCard title="Corrected calcium" value={results.caCorr} unit="mg/dL" formula="Ca + 0.8 × (4 − Albumin)" onPush={() => push("Corrected Calcium", results.caCorr, "mg/dL")}
                onCopyToRx={rx("corr_ca", "Corrected Calcium", results.caCorr, "mg/dL", "")}
                pinned={isPinned("corr_ca")}
                onTogglePin={() => togglePin("corr_ca", RESULT_MAP.find(x => x.key === "corr_ca").title, RESULT_MAP.find(x => x.key === "corr_ca").value, RESULT_MAP.find(x => x.key === "corr_ca").unit)} />
              <ResultCard title="FIB-4" value={results.fib} interp={fib4Interp(results.fib)} formula="Age × AST / (Platelets × √ALT)" onPush={() => push("FIB-4", results.fib, "")}
                onCopyToRx={rx("fib4", "FIB-4", results.fib, "", fib4Interp(results.fib))}
                pinned={isPinned("fib4")}
                onTogglePin={() => togglePin("fib4", RESULT_MAP.find(x => x.key === "fib4").title, RESULT_MAP.find(x => x.key === "fib4").value, RESULT_MAP.find(x => x.key === "fib4").unit)} />
              <ResultCard title="BMI" value={results.bmiVal} unit="kg/m²" interp={bmiCategory(results.bmiVal)} formula="Weight / Height²" onPush={() => push("BMI", results.bmiVal, "kg/m²")}
                onCopyToRx={rx("bmi", "BMI", results.bmiVal, "kg/m²", bmiCategory(results.bmiVal))}
                pinned={isPinned("bmi")}
                onTogglePin={() => togglePin("bmi", RESULT_MAP.find(x => x.key === "bmi").title, RESULT_MAP.find(x => x.key === "bmi").value, RESULT_MAP.find(x => x.key === "bmi").unit)} />
              <ResultCard title="BSA (Mosteller)" value={results.bsa} unit="m²" formula="√(Ht × Wt / 3600)" onPush={() => push("BSA (Mosteller)", results.bsa, "m²")}
                onCopyToRx={rx("bsa", "BSA (Mosteller)", results.bsa, "m²", "")}
                pinned={isPinned("bsa")}
                onTogglePin={() => togglePin("bsa", RESULT_MAP.find(x => x.key === "bsa").title, RESULT_MAP.find(x => x.key === "bsa").value, RESULT_MAP.find(x => x.key === "bsa").unit)} />
              <ResultCard title="WHR" value={results.whrVal} interp={results.whrVal !== null ? (sex && String(sex).toUpperCase().startsWith("F") ? (results.whrVal > 0.85 ? "High" : "OK") : (results.whrVal > 0.9 ? "High" : "OK")) : ""} formula="Waist / Hip" onPush={() => push("WHR", results.whrVal, "")}
                onCopyToRx={rx("whr", "WHR", results.whrVal, "", results.whrVal !== null ? (sex && String(sex).toUpperCase().startsWith("F") ? (results.whrVal > 0.85 ? "High" : "OK") : (results.whrVal > 0.9 ? "High" : "OK")) : "")}
                pinned={isPinned("whr")}
                onTogglePin={() => togglePin("whr", RESULT_MAP.find(x => x.key === "whr").title, RESULT_MAP.find(x => x.key === "whr").value, RESULT_MAP.find(x => x.key === "whr").unit)} />
              {/* ------- 2026-02 EXPANSION: 24 new lab-auto-fill calculators ------- */}
              {NEW_RESULT_CARDS.map((c) => (
                <ResultCard
                  key={c.key}
                  title={c.title}
                  value={c.value}
                  unit={c.unit || ""}
                  interp={c.interp || ""}
                  formula={c.formula || ""}
                  onPush={() => push(c.title, c.value, c.unit || "")}
                  onCopyToRx={rx(c.key, c.title, c.value, c.unit || "", c.interp || "")}
                  pinned={isPinned(c.key)}
                  onTogglePin={() => togglePin(c.key, c.title, c.value, c.unit || "")}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-slate-100 gap-2 flex-wrap">
            <div className="text-[10px] text-slate-500 flex-1 min-w-[240px]">
              Values auto-fill from investigations &amp; vitals above; override any field to run a what-if. Patient: <strong>{age || "—"} yr</strong>, sex <strong>{sex || "—"}</strong>.
            </div>
            <button
              type="button"
              onClick={resetFromLabs}
              className="text-[11px] px-2.5 py-1 rounded-md border border-pink-200 bg-pink-50 text-pink-700 hover:bg-pink-100 inline-flex items-center gap-1"
              data-testid="calc-sync-from-labs-btn"
              title="Discard all what-if overrides and re-sync every calculator field from the current Investigations table"
            >
              Sync from labs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
